import { useEffect, useLayoutEffect, useRef, useState, type ReactElement } from 'react';
import type { RoomMember, RoomReaction } from '../api';
import { PresenceAvatar } from './PresenceAvatar';
import './RoomReactions.css';

/**
 * The quick bar a long press (or a right click) on a room line opens (小屋留人需求 需求四,
 * 2026-10-19; Google Chat's): the room's first five emoji and More — the small grid of
 * the whole set — floating over the bubble it came from, under it when the bubble is at
 * the top of the screen. Picking the emoji this person already has there takes it off.
 * Escape, a tap outside it, or the thread scrolling puts it away. Groups (2026-11-03) use the same bar for a post or an
 * answer: `label` names what is reacted to, `moreLog` its More, and `roomId` is the group's id there.
 */
export function RoomReactionBar({ anchor, set, quick = 5, mine, onPick, onClose, roomId, label = 'React to this line', moreLog = 'room_reaction_more' }: {
  anchor: HTMLElement;
  set: string[];
  quick?: number;
  mine: string | null;
  onPick: (emoji: string, from: 'bar' | 'grid') => void;
  onClose: () => void;
  roomId: string;
  label?: string;
  moreLog?: string;
}): ReactElement {
  const ref = useRef<HTMLDivElement | null>(null);
  const [more, setMore] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const a = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const gap = 8;
    const top = a.top - h - gap >= 8 ? a.top - h - gap : Math.min(a.bottom + gap, window.innerHeight - h - 8);
    const left = Math.max(8, Math.min(a.left, window.innerWidth - w - 8));
    setPos({ top, left });
  }, [anchor, more]);

  // The caller's onClose is a fresh function each render; the listeners are set once, for the bar's life.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const close = (): void => closeRef.current();
    const away = (e: Event): void => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
    const key = (e: KeyboardEvent): void => { if (e.key === 'Escape') close(); };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', close, true);
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', close, true);
    };
  }, []);

  const pick = (emoji: string, from: 'bar' | 'grid'): void => { onPick(emoji, from); onClose(); };
  return (
    <div ref={ref} className={`room-react-bar${more ? ' is-more' : ''}`} role="menu" aria-label={label}
      style={pos ? { top: pos.top, left: pos.left } : { visibility: 'hidden', top: 0, left: 0 }}>
      {!more ? (
        <>
          {set.slice(0, quick).map((e) => (
            <button key={e} type="button" role="menuitem" className={`room-react-pick${mine === e ? ' is-mine' : ''}`} aria-label={mine === e ? `Take off ${e}` : `React ${e}`}
              onClick={() => pick(e, 'bar')}>{e}</button>
          ))}
          <button type="button" role="menuitem" className="room-react-more" aria-label="More" data-log={moreLog} data-log-match-id={roomId} onClick={() => setMore(true)}>
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden><path d="M10 4.5v11M4.5 10h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none" /></svg>
          </button>
        </>
      ) : (
        <div className="room-react-grid">
          {set.map((e) => (
            <button key={e} type="button" role="menuitem" className={`room-react-pick${mine === e ? ' is-mine' : ''}`} aria-label={mine === e ? `Take off ${e}` : `React ${e}`}
              onClick={() => pick(e, 'grid')}>{e}</button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * The emoji under a line (需求四): a capsule each — the emoji and how many — at the
 * bubble's lower edge, this person's own a shade warmer. A tap puts that emoji on or
 * takes theirs off; holding it (or hovering, with a mouse) says who.
 */
export function RoomReactionCapsules({ reactions, onToggle, roomId, align }: {
  reactions: RoomReaction[];
  onToggle?: (emoji: string) => void;
  roomId: string;
  align: 'start' | 'end';
}): ReactElement | null {
  const [who, setWho] = useState<string | null>(null);
  const timer = useRef<number | null>(null);
  if (!reactions.length) return null;
  const hold = (emoji: string): void => {
    timer.current = window.setTimeout(() => { timer.current = null; setWho(emoji); }, 450);
  };
  const release = (): void => { if (timer.current !== null) window.clearTimeout(timer.current); timer.current = null; };
  const shown = reactions.find((r) => r.emoji === who);
  return (
    <div className={`room-react-caps is-${align}`}>
      {reactions.map((r) => (
        <button key={r.emoji} type="button" className={`room-react-cap${r.mine ? ' is-mine' : ''}`} disabled={!onToggle}
          title={r.by.join(', ')} aria-label={`${r.emoji} ${r.count}${r.mine ? ', yours' : ''} — ${r.by.join(', ')}`}
          onPointerDown={(e) => { if (e.pointerType !== 'mouse') hold(r.emoji); }} onPointerUp={release} onPointerLeave={release} onPointerCancel={release}
          onContextMenu={(e) => { e.preventDefault(); setWho(r.emoji); }}
          onClick={() => {
            if (who) { setWho(null); return; }
            if (onToggle) onToggle(r.emoji);
          }}
          data-log={who ? undefined : 'room_reaction_capsule'} data-log-match-id={roomId}>
          <span aria-hidden>{r.emoji}</span>{r.count > 1 && <span className="room-react-n">{r.count}</span>}
        </button>
      ))}
      {shown && <span className="room-react-who" role="status" data-log-seen="room_reaction_who" data-log-match-id={roomId} onClick={() => setWho(null)} dir="auto">{shown.emoji} {shown.by.join(', ')}</span>}
    </div>
  );
}

/** The faces of the others who have read up to this line (需求三): 16 px, stacked from the right, at the bubble's lower corner. No time, no names on the page. */
export function RoomReaders({ readers }: { readers: RoomMember[] }): ReactElement | null {
  if (!readers.length) return null;
  return (
    <span className="room-readers" aria-label={`Read by ${readers.map((r) => r.name).join(', ')}`}>
      {readers.map((r) => <span key={r.name} className="room-reader"><PresenceAvatar avatarKey={r.avatar} size={16} /></span>)}
    </span>
  );
}
